import React from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useOrganizationEditPage } from 'apps/sigma2-ui/src/organizations/lib/useOrganizationEditPage';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { SaveIndicator } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { ImageCropper } from 'apps/sigma2-ui/src/components/ui/ImageCropper';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const OrganizationEditPage = () => {
  const language = useLanguage();

  const {
    setName,
    name,
    submitButtonLabel,
    submitButtonDisabled,
    submitButtonBehavior,
    saveIndicatorStatus,
    manageDatabasesButtonBehavior,
    manageDatabasesButtonLabel,
    handleCropComplete,
    uploadedImageUrl,
    isUploading,
    selectedFile,
    setSelectedFile,
    showCropper,
    setShowCropper,
    dragActive,
    handleFileSelect,
    handleDrag,
    handleDrop,
    handleRemoveImage,
    organizationId,
    manageEntitlementsButtonBehavior,
    manageEntitlementsButtonLabel,
    manageConnectionsButtonBehavior,
    manageConnectionsButtonLabel,
  } = useOrganizationEditPage();

  return (
    <AdminLayout currentPage={'OrganizationEdit'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Edit Organization',
            to: `/organizations/edit/${organizationId}`,
          },
        ]}
      />
      <Spacing size={'xs'} direction={'vertical'}>
        <S2Typography
          translatedContent={language.organizationLogoEdit()}
          variant="headline-4"
        />
        <S2Typography
          translatedContent={language.organizationLogoEditDetails()}
          variant="body-6"
        />

        <form className="space-y-6 max-w-2xl">
          <table className="w-full">
            <tbody>
              <tr>
                <td className="py-4 pr-8 align-top">
                  <S2Typography
                    translatedContent={language.organizationName()}
                    variant="headline-6-alt"
                  />
                  <SimpleInput
                    label=""
                    value={name}
                    onChange={setName}
                    placeholder="Enter organization name"
                    extrasClassName="w-full"
                  />
                </td>
              </tr>
              <tr>
                <td className="py-4 align-top">
                  <S2Typography
                    translatedContent={language.organizationLogo()}
                    variant="headline-6-alt"
                  />

                  {!uploadedImageUrl && !showCropper && (
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                        ${dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary'}
                      `}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById('file-upload')?.click()
                      }
                    >
                      <input
                        id="file-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="form-input hidden"
                      />
                      <div className="space-y-2">
                        <S2Typography
                          translatedContent={language.organizationLogoDragAndDrop()}
                          variant="body-5"
                        />
                        <S2Typography
                          variant="small-label"
                          translatedContent={language.organizationLogoFileTypes()}
                        />
                      </div>
                    </div>
                  )}

                  {showCropper && selectedFile && (
                    <div className="border rounded-lg p-4">
                      <S2Typography
                        variant="body-6"
                        translatedContent={language.organizationLogoAdjustCrop()}
                      />
                      <ImageCropper
                        imageUrl={selectedFile}
                        onCropComplete={(blob) => {
                          setShowCropper(false);
                          handleCropComplete(blob);
                        }}
                        onCancel={() => {
                          setShowCropper(false);
                          setSelectedFile(null);
                        }}
                      />
                    </div>
                  )}

                  {uploadedImageUrl && !showCropper && (
                    <div className="border rounded-lg p-4 inline-block">
                      <img
                        src={uploadedImageUrl}
                        alt="Organization logo preview"
                        className="w-32 h-32 object-cover rounded mb-2"
                      />
                      <Button
                        text="Remove Logo"
                        behavior={{
                          type: 'onClick',
                          onClick: handleRemoveImage,
                        }}
                        className="mt-4"
                        variant="outline"
                        size="small"
                      />
                    </div>
                  )}

                  {isUploading && (
                    <div className="mt-2">
                      <S2Typography
                        variant="small-label"
                        translatedContent={language.organizationLogoUploading()}
                      />
                    </div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="py-4 align-top">
                  <S2Typography
                    translatedContent={language.editDatabasesTitle()}
                    variant="headline-6-alt"
                  />
                  <Button
                    behavior={manageDatabasesButtonBehavior}
                    text={manageDatabasesButtonLabel}
                    size="small"
                  />
                </td>
              </tr>
              <tr>
                <td className="py-4 align-top">
                  <S2Typography
                    translatedContent={language.manageExternalEntitlements()}
                    variant="headline-6-alt"
                  />
                  <Button
                    behavior={manageEntitlementsButtonBehavior}
                    text={manageEntitlementsButtonLabel}
                    size="small"
                  />
                </td>
              </tr>
              <tr>
                <td className="py-4 align-top">
                  <S2Typography
                    translatedContent={language.manageOrganizationConnectionsPageTitle()}
                    variant="headline-6-alt"
                  />
                  <Button
                    behavior={manageConnectionsButtonBehavior}
                    text={manageConnectionsButtonLabel}
                    size="small"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="flex justify-end">
            <Spacing size={'md'} direction={'horizontal'}>
              <Button
                behavior={submitButtonBehavior}
                text={submitButtonLabel}
                disabled={submitButtonDisabled || isUploading}
              />
              <SaveIndicator status={saveIndicatorStatus} />
            </Spacing>
          </div>

          <div className="text-gray-500">
            <S2Typography
              variant="body-7"
              translatedContent={language.requiredFieldsIndicator()}
            />
          </div>
        </form>
      </Spacing>
    </AdminLayout>
  );
};
